type RuntimeConfig = Readonly<Record<string, string | undefined>>;

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }

  // Same shape as vite/client, which the type-check does not load.
  interface ImportMetaEnv {
    [key: string]: any;
  }

  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

const modes = ["LOCAL", "DEV", "PROD"] as const;

const isMode = (value: string | null | undefined): value is (typeof modes)[number] =>
  !!value && modes.includes(value as (typeof modes)[number]);

const runtimeConfig =
  typeof window === "undefined" ? undefined : window.__RUNTIME_CONFIG__;

const nonEmpty = (value: string | undefined) =>
  typeof value === "string" && value.length > 0 ? value : undefined;

const getRuntimeValue = (key: string) => nonEmpty(runtimeConfig?.[key]);
const getBuildValue = (key: string) => nonEmpty(import.meta.env[key]);

export let configMode = localStorage.getItem("configMode");

localStorage.removeItem("configMode");

if (!isMode(configMode)) {
  configMode =
    getRuntimeValue("VITE_CONFIG_MODE") ??
    getBuildValue("VITE_CONFIG_MODE") ??
    "PROD";
}

type Config = {
  VITE_MC_APP_NAME: string;
  [key: string]: string;
};

export const config = new Proxy<Config>(
  {} as Config,
  {
    get: (_, key) => {
      if (key === "mode") {
        return configMode;
      }

      if (typeof key !== "string") {
        return undefined;
      }

      const modeKey = key.replace("VITE_", `VITE_${configMode}_`);

      return (
        getRuntimeValue(modeKey) ??
        getRuntimeValue(key) ??
        getBuildValue(modeKey) ??
        getBuildValue(key)
      );
    },
    set: (_, prop, value) => {
      if (
        value &&
        prop === "mode" &&
        isMode(value) &&
        value !== configMode
      ) {
        configMode = value;
        console.warn(
          "Change detected on CONFIG.mode, the page will be refreshed..."
        );
        localStorage.setItem("configMode", value);
        window.location.reload();

        return true;
      }

      return false;
    },
  }
);
